/**
 * EnsoMirai++ — COCKPIT_* token names.
 *
 * Kept for the many screens that import them, but every value now aliases
 * design-system/foundation.ts so this kit can no longer drift from the others.
 */

import { alpha } from '@mui/material/styles';
import {
  BORDER,
  DOMAIN_HEX,
  ELEVATION,
  INK,
  MOTION,
  RADIUS,
  SPACE,
  STATUS_HEX,
  SURFACE,
  TYPE,
} from '../foundation';

/* ── 1. Engineering Cockpit Color Grammar ────────────────────── */

export const COCKPIT_COLORS = {
  verified: STATUS_HEX.success,
  verifiedMuted: 'rgba(34, 217, 137, 0.16)',
  verifiedSubtle: 'rgba(34, 217, 137, 0.07)',

  /**
   * @deprecated Misleading name: this is the amber *warning* hue. For
   * "in progress" use `info` (see GUIDELINES.md §4) or StatusBadge tone="info".
   */
  running: STATUS_HEX.warning,
  runningMuted: 'rgba(245, 180, 0, 0.16)',
  runningSubtle: 'rgba(245, 180, 0, 0.07)',

  info: STATUS_HEX.info,
  infoMuted: 'rgba(77, 138, 240, 0.16)',
  infoSubtle: 'rgba(77, 138, 240, 0.07)',

  critical: STATUS_HEX.error,
  criticalMuted: 'rgba(242, 85, 106, 0.16)',
  criticalSubtle: 'rgba(242, 85, 106, 0.07)',

  // Accents
  cyan: '#2EB8E6',
  violet: STATUS_HEX.policy,
  orange: DOMAIN_HEX.operate,
  lime: '#8FD94A',
  pink: '#E879A9',

  ink: INK.primary,
  secondary: INK.secondary,
  tertiary: INK.tertiary,
  ghost: INK.ghost,
  muted: INK.ghost,
} as const;

export const DOMAIN_THEME = DOMAIN_HEX;

/* ── 2. Canvas & Surfaces (theme-aware) ──────────────────────── */

export const COCKPIT_SURFACES = {
  appBg: SURFACE.app,
  workspaceBg: 'var(--diagram-bg)',
  panelBg: SURFACE.panel,
  cardBg: SURFACE.card,
  hoverBg: SURFACE.hover,
  selectedBg: SURFACE.selected,
  overlayBg: SURFACE.overlay,
  /** Deep stage behind colorful nodes / graphs */
  diagramStage: 'var(--diagram-stage, #070a10)',
  diagramGrid: 'var(--diagram-grid, rgba(148, 176, 210, 0.055))',
} as const;

export const COCKPIT_BORDERS = {
  subtle: BORDER.subtle,
  default: BORDER.default,
  strong: BORDER.strong,
  inset: 'var(--border-inset)',
} as const;

/** Standard bordered panel chrome for MUI sx */
export const COCKPIT_PANEL_CHROME = {
  bgcolor: SURFACE.panel,
  border: `1px solid ${BORDER.default}`,
  boxShadow: ELEVATION.sm,
  boxSizing: 'border-box' as const,
} as const;

/* ── 3. Spacing & Border Radii ──────────────────────────────── */

export const COCKPIT_SPACE = {
  xs: SPACE.xs,
  sm: SPACE.sm,
  md: SPACE.md,
  lg: SPACE.lg,
  xl: SPACE.xl,
  '2xl': SPACE['2xl'],
} as const;

export const COCKPIT_RADIUS = {
  sm: RADIUS.chip,
  md: RADIUS.control,
  lg: RADIUS.card,
  xl: RADIUS.dialog,
} as const;

/* ── 4. Typography Scale ─────────────────────────────────────── */

export const COCKPIT_TYPE = {
  display: TYPE.display,
  pageTitle: TYPE.pageTitle,
  sectionTitle: TYPE.sectionTitle,
  subheading: TYPE.subheading,
  /** Legacy alias */
  h3: TYPE.subheading,
  subtitle: TYPE.caption,
  body: TYPE.body,
  caption: TYPE.caption,
  overline: TYPE.overline,
  mono: TYPE.mono,
  metric: TYPE.metric,
  metricSm: TYPE.metricSm,
} as const;

/* ── 5. Motion ───────────────────────────────────────────────── */

export const COCKPIT_MOTION = {
  fast: MOTION.fast,
  normal: MOTION.normal,
  slow: MOTION.slow,
  /** Framer-motion compatible cubic-bezier tuple */
  easing: MOTION.easing,
} as const;

/* ── 6. Elevation ────────────────────────────────────────────── */

export const COCKPIT_ELEVATION = {
  sm: ELEVATION.sm,
  md: ELEVATION.md,
  lg: ELEVATION.lg,
  panel: 'var(--panel-shadow)',
} as const;

/* ── Helper Functions ────────────────────────────────────────── */

export function getCockpitColor(status: 'verified' | 'running' | 'info' | 'critical'): string {
  return COCKPIT_COLORS[status];
}

export function glowShadow(color: string, intensity = 0.2): string {
  return `0 0 12px ${safeAlpha(color, intensity)}`;
}

/**
 * Like MUI alpha(), but supports CSS variables via color-mix.
 * MUI alpha() throws on `var(--…)` and blanks the app.
 */
export function safeAlpha(color: string, opacity: number): string {
  const o = Math.min(1, Math.max(0, opacity));
  if (!color || typeof color !== 'string') {
    return `rgba(148, 163, 184, ${o})`;
  }
  if (color.startsWith('var(') || color.startsWith('color-mix(')) {
    return `color-mix(in srgb, ${color} ${Math.round(o * 100)}%, transparent)`;
  }
  return alpha(color, o);
}

/** Theme-aware surface opacity — works with CSS variable surfaces */
export function surfaceMix(
  surface: 'panel' | 'diagram' | 'card' | 'app' | 'hover',
  opacity = 1,
): string {
  const v = {
    panel: SURFACE.panel,
    diagram: 'var(--diagram-bg)',
    card: SURFACE.card,
    app: SURFACE.app,
    hover: SURFACE.hover,
  }[surface];
  if (opacity >= 0.995) return v;
  return `color-mix(in srgb, ${v} ${Math.round(opacity * 100)}%, transparent)`;
}
